import React from 'react';
import gettext from 'js-utils/gettext';
import flowRight from 'lodash/flowRight';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';

import Modal from 'eventbrite_design_system/modal/Modal';
import Illustration from 'eventbrite_design_system/illustration/Illustration';
import { STYLE_STATIC } from 'eventbrite_design_system/inputField/constants';
import InputField from 'eventbrite_design_system/inputField/InputField';
import SelectField from 'eventbrite_design_system/inputField/SelectField';
import NotificationBar from 'eventbrite_design_system/notification/NotificationBar';
import ValidationFormField from 'eventbrite_design_system/validationFormField/ValidationFormField';

import withReduxForm from 'eventbrite_design_system/form/withReduxForm';
import PressButtonBlueSVG from './components/illustrations/pressButtonBlueSVG';
import connectSalesStatusForm from './connectSalesStatusForm';
import { constants } from '@eb/event-create-publish-utils';
import {
    CUSTOM_MESSAGE_MAX_LENGTH,
    FIELD_NAMES,
    FORM_NAME,
    LOCALIZED_TEXT,
    STATUS_OPTION_PROP_TYPE,
    REDIRECT_TO_EMAIL,
} from './constants';
import validate from './validate';
import { getOnSaleWarningNotificationTextForStatus } from './utils';

import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';
import StopwatchChunkySvg from 'eventbrite_design_system/iconography/icons/StopwatchChunky';
const {
    DISPLAY_SALES_STATUS_CANCELLED,
    DISPLAY_SALES_STATUS_POSTPONED,
} = constants;
const HELP_LINK_STATUSES = [
    DISPLAY_SALES_STATUS_CANCELLED,
    DISPLAY_SALES_STATUS_POSTPONED,
];
const NotificationHelpLink = ({ status }) => {
    if (!includes(HELP_LINK_STATUSES, status)) {
        return null;
    }
    return (
        <span data-spec="notification-help-link">
            {' '}
            <a href="/support/articleredirect?anum=41550" target="_blank">
                {gettext('Learn More')}
            </a>
        </span>
    );
};

const SalesStatusWarning = ({
    selectedStatusCode,
    willTakeOffSale,
    wontTakeOffSale,
    changingSalesStatusIsBlocked,
}) => {
    let notification = null;

    if (changingSalesStatusIsBlocked) {
        notification = (
            <div className="eds-l-pad-top-2">
                <NotificationBar iconType={<AlertChunkySvg />}>
                    <span>
                        {
                            LOCALIZED_TEXT.warningNotificationTextChangingStatusDisabled
                        }
                    </span>
                </NotificationBar>
            </div>
        );
    } else if (selectedStatusCode) {
        if (willTakeOffSale) {
            notification = (
                <NotificationBar iconType={<AlertChunkySvg />}>
                    <span>
                        {getOnSaleWarningNotificationTextForStatus(
                            selectedStatusCode,
                        )}
                    </span>
                    {
                        <span className="eds-l-pad-left-1">
                            {REDIRECT_TO_EMAIL}
                        </span>
                    }
                    <NotificationHelpLink status={selectedStatusCode} />
                </NotificationBar>
            );
        } else if (wontTakeOffSale) {
            notification = (
                <NotificationBar iconType={<StopwatchChunkySvg />}>
                    <span>
                        {LOCALIZED_TEXT.warningNotificationTextScheduled}
                    </span>
                </NotificationBar>
            );
        }
    }

    return notification;
};

const SalesStatusModalContent = ({
    statusOptions,
    selectedStatusCode,
    willTakeOffSale,
    wontTakeOffSale,
    isCustomSelected,
    changingSalesStatusIsBlocked,
    onStatusCodeChange,
}) => {
    const salesStatusWarning = (
        <SalesStatusWarning
            selectedStatusCode={selectedStatusCode}
            willTakeOffSale={willTakeOffSale}
            wontTakeOffSale={wontTakeOffSale}
            changingSalesStatusIsBlocked={changingSalesStatusIsBlocked}
        />
    );
    let customMessageField;
    let placeholder = LOCALIZED_TEXT.selectorPlaceholder;

    if (isCustomSelected) {
        customMessageField = (
            <ValidationFormField
                name={FIELD_NAMES.customMessage}
                v2={true}
                label={LOCALIZED_TEXT.customMessageLabel}
            >
                <InputField
                    style={STYLE_STATIC}
                    id="customMessage"
                    label=""
                    maxLength={CUSTOM_MESSAGE_MAX_LENGTH}
                    hasCharacterCount={true}
                />
            </ValidationFormField>
        );
    }

    if (changingSalesStatusIsBlocked) {
        placeholder = LOCALIZED_TEXT.selectorPlaceholderUnavailable;
    }

    return (
        <form data-spec={FORM_NAME} className="eds-l-pad-all-6">
            <h3 className="eds-text-hs">{LOCALIZED_TEXT.headline}</h3>
            <p className="eds-l-mar-vert-4">{LOCALIZED_TEXT.description}</p>
            <ValidationFormField name={FIELD_NAMES.statusCode} v2={true}>
                <SelectField
                    label={LOCALIZED_TEXT.selectorLabel}
                    placeholder={placeholder}
                    style={STYLE_STATIC}
                    id="statusCode"
                    onChange={onStatusCodeChange}
                    values={statusOptions}
                    disabled={changingSalesStatusIsBlocked}
                />
            </ValidationFormField>
            {customMessageField}
            {salesStatusWarning}
        </form>
    );
};

const SalesStatusIllustration = (
    <div className="modal-sales-status-illustration-wrapper">
        <Illustration
            type={<PressButtonBlueSVG />}
            width="auto"
            height="100%"
        />
    </div>
);

const _getButtonLabel = (willTakeOffSale) => {
    let confirmLabel = LOCALIZED_TEXT.modalNotOnSaleConfirmLabel;

    if (willTakeOffSale) {
        confirmLabel = LOCALIZED_TEXT.modalOnSaleConfirmLabel;
    }

    return confirmLabel;
};

class SalesStatusForm extends React.PureComponent {
    static propTypes = {
        onClose: PropTypes.func.isRequired,
        shouldShowModal: PropTypes.bool.isRequired,
        selectedStatusCode: PropTypes.string,
        statusOptions: PropTypes.arrayOf(STATUS_OPTION_PROP_TYPE).isRequired,
        isCustomSelected: PropTypes.bool,
        willTakeOffSale: PropTypes.bool,
        wontTakeOffSale: PropTypes.bool,
        changingSalesStatusIsBlocked: PropTypes.bool,
        onFormLoad: PropTypes.func.isRequired,
        salesStatusCode: PropTypes.string,
        salesStatusMessage: PropTypes.string,
        onStatusCodeChange: PropTypes.func,
        hasSalesStatusFormChanged: PropTypes.bool,

        // Provided by redux-form
        handleSubmit: PropTypes.func.isRequired,
        reset: PropTypes.func.isRequired,
        trackGAChangeSalesStatusEvent: PropTypes.func,
        trackGASelectPostponeStatusEvent: PropTypes.func,
    };

    componentDidMount() {
        this.props.onFormLoad();
    }

    componentWillReceiveProps(nextProps) {
        const { salesStatusCode, salesStatusMessage, onFormLoad } = this.props;
        const {
            salesStatusCode: nextSalesStatusCode,
            salesStatusMessage: nextSalesStatusMessage,
        } = nextProps;

        if (
            salesStatusCode !== nextSalesStatusCode ||
            salesStatusMessage !== nextSalesStatusMessage
        ) {
            onFormLoad();
        }
    }

    _handleStatusCodeChange = (val) => {
        const {
            onStatusCodeChange,
            trackGASelectPostponeStatusEvent,
        } = this.props;

        if (val === DISPLAY_SALES_STATUS_POSTPONED) {
            trackGASelectPostponeStatusEvent();
        }

        onStatusCodeChange(val);
    };

    _handleSaveSalesStatus = () => {
        const {
            handleSubmit,
            onClose,
            trackGAChangeSalesStatusEvent,
        } = this.props;

        trackGAChangeSalesStatusEvent();

        handleSubmit();

        onClose();
    };

    _handleCancelSalesStatusModal = () => {
        const { reset, onClose } = this.props;

        reset();

        onClose();
    };

    render() {
        const {
            selectedStatusCode,
            isCustomSelected,
            willTakeOffSale,
            wontTakeOffSale,
            statusOptions,
            invalid,
            submitting,
            shouldShowModal,
            changingSalesStatusIsBlocked,
            hasSalesStatusFormChanged,
        } = this.props;
        const isChangeSalesStatusDisabled =
            invalid || submitting || !hasSalesStatusFormChanged;

        return (
            <Modal
                isShown={shouldShowModal}
                onClose={this._handleCancelSalesStatusModal}
                onPrimaryAction={this._handleSaveSalesStatus}
                onSecondaryAction={this._handleCancelSalesStatusModal}
                primaryIsDisabled={isChangeSalesStatusDisabled}
                primaryText={_getButtonLabel(willTakeOffSale)}
                primaryType="fill"
                secondaryText={LOCALIZED_TEXT.modalCancelLabel}
                secondaryType="neutral"
                illustration={SalesStatusIllustration}
                noPadding={true}
            >
                <SalesStatusModalContent
                    selectedStatusCode={selectedStatusCode}
                    willTakeOffSale={willTakeOffSale}
                    wontTakeOffSale={wontTakeOffSale}
                    isCustomSelected={isCustomSelected}
                    statusOptions={statusOptions}
                    onStatusCodeChange={this._handleStatusCodeChange}
                    changingSalesStatusIsBlocked={changingSalesStatusIsBlocked}
                />
            </Modal>
        );
    }
}

export { SalesStatusForm as UnconnectedSalesStatusForm };
export default flowRight(
    connectSalesStatusForm,
    withReduxForm({
        form: FORM_NAME,
        validate,
    }),
)(SalesStatusForm);
